import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from './Modal';
import WhoAreWe from './WhoAreWe';
import WorkingHoursInfo from './WorkingHoursInfo';
import FollowUs from './FollowUs';
import OurProducts from './OurProducts';
import OurReviews from './OurReviews';
import HygieneRatingModal from './HygieneRatingModal';
import { getAdminPassword } from '../shared/authConfig';
import './TopMenu.css';

type MenuOption = 'whoAreWe' | 'workingHours' | 'followUs' | 'ourProducts' | 'ourReviews' | 'hygieneRating' | null;

const TopMenu: React.FC = () => {
    const [activeModal, setActiveModal] = useState<MenuOption>(null);
    const [isStoreOpen, setIsStoreOpen] = useState<boolean | null>(null);
    const [hasAdminAccess, setHasAdminAccess] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Check if user has admin access
    useEffect(() => {
        const adminPass = getAdminPassword();
        setHasAdminAccess(!!adminPass);
    }, []);

    // Fetch store opening status
    useEffect(() => {
        const checkOpeningStatus = async () => {
            try {
                const response = await fetch('/api/opening-hours/status');
                if (response.ok) {
                    const status = await response.json();
                    setIsStoreOpen(status.isOpen);
                }
            } catch (error) {
                console.error('Failed to fetch opening hours status:', error);
            }
        };

        checkOpeningStatus();
    }, []);

    // Handle modal state when navigating from home page with modal flag
    useEffect(() => {
        // Only show modal if we're on the home page with isModal state
        if (location.pathname === '/' && location.state?.fromPath) {
            const fromPath = location.state.fromPath;
            if (fromPath === '/who-are-we') {
                setActiveModal('whoAreWe');
            } else if (fromPath === '/working-hours') {
                setActiveModal('workingHours');
            } else if (fromPath === '/follow-us') {
                setActiveModal('followUs');
            } else if (fromPath === '/our-products') {
                setActiveModal('ourProducts');
            } else if (fromPath === '/our-reviews') {
                setActiveModal('ourReviews');
            }
        } else {
            setActiveModal(null);
        }
    }, [location]);

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        // Prevent default navigation
        e.preventDefault();

        // Navigate to the home page with state indicating which modal to show
        navigate('/', { state: { fromPath: path }, replace: false });
    };

    const handleCloseModal = () => {
        setActiveModal(null);
        // Clear the state by navigating to home without state
        navigate('/', { replace: true });
    };

    return (
        <>
            <div className="top-menu">
                <div className="top-menu-container">
                    <Link
                        to="/our-reviews"
                        className="top-menu-link"
                        onClick={(e) => handleLinkClick(e, '/our-reviews')}
                    >
                        Our Reviews
                    </Link>
                    <Link
                        to="/working-hours"
                        className="top-menu-link"
                        onClick={(e) => handleLinkClick(e, '/working-hours')}
                    >
                        {isStoreOpen === true ? 'We are open!' : 'Working Hours'}
                    </Link>
                    <Link
                        to="/follow-us"
                        className="top-menu-link"
                        onClick={(e) => handleLinkClick(e, '/follow-us')}
                    >
                        Follow Us
                    </Link>
                    <button
                        type="button"
                        className="top-menu-link top-menu-link--hygiene"
                        onClick={() => setActiveModal('hygieneRating')}
                        aria-label="View food hygiene rating"
                    >
                        <span className="hygiene-nav-badge-mini">5⭐</span>
                        Hygiene Rated
                    </button>
                    {hasAdminAccess && (
                        <Link
                            to="/melaba"
                            className="top-menu-link admin-link"
                        >
                            🔐 Admin
                        </Link>
                    )}

                </div>
            </div>

            {activeModal === 'whoAreWe' && (
                <Modal isOpen={true} onClose={handleCloseModal}>
                    <h2>Our Story</h2>
                    <WhoAreWe />
                </Modal>
            )}

            {activeModal === 'workingHours' && (
                <Modal
                    isOpen={true}
                    onClose={handleCloseModal}
                    closeOnOverlayClick={true}
                >
                    <div onClick={handleCloseModal} style={{ cursor: 'pointer' }}>
                        <h2>Working Hours</h2>
                        <WorkingHoursInfo />
                    </div>
                </Modal>
            )}
            {activeModal === 'followUs' && (
                <Modal isOpen={true} onClose={handleCloseModal}>
                    <h2>Follow Us</h2>
                    <FollowUs />
                </Modal>
            )}
            {activeModal === 'ourProducts' && (
                <Modal isOpen={true} onClose={handleCloseModal}>
                    <h2>Gift Shop</h2>
                    <OurProducts />
                </Modal>
            )}
            {activeModal === 'ourReviews' && (
                <Modal isOpen={true} onClose={handleCloseModal}>
                    <h2>Our Reviews</h2>
                    <OurReviews />
                </Modal>
            )}

            <HygieneRatingModal
                isOpen={activeModal === 'hygieneRating'}
                onClose={handleCloseModal}
            />


        </>
    );
};

export default TopMenu;
